import { useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useAnalytics } from "../contexts/analytics/hooks";
import { useDraftMode } from "../contexts/DraftModeContext";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import { backendService } from "../services/backend.service";
import { getOtWeekStart } from "../utils/dateUtils";
import type { NurseOvertimeMap } from "../utils/models";
import { useDraftDayStats } from "./useDraftDayStats";

/** True iff [startDate, endDate] overlaps the 7-day OT-week containing *target*. */
const rangeOverlapsOtWeek = (startDate: string, endDate: string, target: DateTime, weekStartDay: number): boolean => {
	const weekStart = getOtWeekStart(target, weekStartDay);
	const weekEndExclusive = weekStart.plus({ days: 7 });
	const rangeStart = DateTime.fromISO(startDate);
	const rangeEnd = DateTime.fromISO(endDate);
	if (!rangeStart.isValid || !rangeEnd.isValid) return false;
	// Range is inclusive on both ends per the existing endpoint's contract.
	return rangeStart < weekEndExclusive && rangeEnd >= weekStart;
};

type UseNurseOvertimeMapResult = {
	data: NurseOvertimeMap | undefined;
	isLoading: boolean;
	error: unknown;
};

export const useNurseOvertimeMap = (
	departmentId: string,
	startDate: string,
	endDate: string,
	timezone: string,
	isActual: boolean,
): UseNurseOvertimeMapResult => {
	const { isDraftMode, activeDayData } = useDraftMode();
	const { chosenDepartment } = useHospitalData();
	const { trackError } = useAnalytics();

	// Live recompute only covers the active day's OT-week; fall back to the API outside that range.
	const draftDateISO = activeDayData?.date.toFormat("yyyy-MM-dd");
	const useDraft = isDraftMode && !!activeDayData && rangeOverlapsOtWeek(startDate, endDate, activeDayData.date, chosenDepartment.week_start_day);
	const draftStats = useDraftDayStats(useDraft ? draftDateISO : undefined);

	const liveQuery = useQuery<NurseOvertimeMap>({
		queryKey: ["nurse-overtime", departmentId, startDate, endDate, isActual],
		queryFn: async () => {
			try {
				const response = await backendService.getNurseOvertime(departmentId, startDate, endDate, timezone, isActual);
				return response.data;
			} catch (error) {
				trackError("nurse_overtime_map_fetch_failed", { error_message: (error as Error)?.message });
				throw error;
			}
		},
		enabled: !useDraft && !!departmentId && !!startDate && !!endDate,
		staleTime: 5 * 60 * 1000,
	});

	if (useDraft) {
		return {
			data: draftStats.data?.nurseOvertime ?? {},
			isLoading: draftStats.isLoading,
			error: draftStats.error,
		};
	}
	return { data: liveQuery.data, isLoading: liveQuery.isLoading, error: liveQuery.error };
};
